import { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
} from 'recharts';
import api from '../services/api';

type ToolKey = 'liker' | 'commenter' | 'subscriber';

interface DashboardStats {
  accounts: { total: number; active: number };
  configs: Record<
    ToolKey,
    { total: number; active: number; today: number; allTime: number }
  >;
  today: {
    byType: Record<string, { success: number; failed: number }>;
    total: number;
  };
  series7d: { date: string; like: number; comment: number; subscribe: number }[];
  recentActions: any[];
}

const TOOLS: { key: ToolKey; title: string; icon: any; color: string }[] = [
  { key: 'liker', title: 'Автолайкер', icon: <FavoriteIcon />, color: '#f04438' },
  { key: 'commenter', title: 'Автокомментатор', icon: <CommentIcon />, color: '#9b6bff' },
  { key: 'subscriber', title: 'Автоподписчик', icon: <PersonAddIcon />, color: '#f79009' },
];

const TYPE_LABEL: Record<string, { ru: string; tool: ToolKey }> = {
  like: { ru: 'Лайки', tool: 'liker' },
  comment: { ru: 'Комменты', tool: 'commenter' },
  subscribe: { ru: 'Подписки', tool: 'subscriber' },
};

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh on every focus return — лучше чем polling, прозрачно для пользователя
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  if (!stats) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Format ISO date "2026-05-28" → "28.05" for chart axis
  const formatChartDate = (iso: string) => {
    const [, m, d] = iso.split('-');
    return `${d}.${m}`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Дашборд
        </Typography>
        <Tooltip title="Обновить данные">
          <span>
            <IconButton onClick={load} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Top row: accounts + today total */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountCircleIcon color="action" />
                <Typography variant="overline" color="text.secondary">
                  VK Аккаунты
                </Typography>
              </Box>
              <Typography variant="h4">{stats.accounts.total}</Typography>
              <Typography variant="caption" color="text.secondary">
                из них активны: <b>{stats.accounts.active}</b>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography variant="overline" sx={{ opacity: 0.85 }}>
                Действий сегодня
              </Typography>
              <Typography variant="h4">{stats.today.total}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                лайки + комментарии + подписки
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Per-type today */}
        {Object.entries(stats.today.byType).map(([type, counts]) => {
          const label = TYPE_LABEL[type]?.ru || type;
          const tool = TYPE_LABEL[type]?.tool;
          const cfg = tool ? stats.configs[tool] : null;
          return (
            <Grid item xs={12} sm={6} md={2} key={type}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {label} сегодня
                  </Typography>
                  <Typography variant="h5" color={counts.success > 0 ? 'success.main' : undefined}>
                    {counts.success}
                  </Typography>
                  {counts.failed > 0 && (
                    <Typography variant="caption" color="error.main">
                      ошибок: {counts.failed}
                    </Typography>
                  )}
                  {cfg && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      конфигов: {cfg.active}/{cfg.total} активны
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Per-tool quick rollup with link */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {TOOLS.map(({ key, title, icon, color }) => {
          const c = stats.configs[key];
          return (
            <Grid item xs={12} md={4} key={key}>
              <Card sx={{ borderLeft: 4, borderColor: color }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ color }}>{icon}</Box>
                    <Typography variant="subtitle1">{title}</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Chip
                      label={c.active > 0 ? `${c.active} активн.` : 'нет активных'}
                      size="small"
                      color={c.active > 0 ? 'success' : 'default'}
                      variant={c.active > 0 ? 'filled' : 'outlined'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Сегодня
                      </Typography>
                      <Typography variant="h5">{c.today}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Всего
                      </Typography>
                      <Typography variant="h5">{c.allTime}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Конфигов
                      </Typography>
                      <Typography variant="h5">{c.total}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* 7-day chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Динамика за 7 дней
          </Typography>
          <Box sx={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <LineChart
                data={stats.series7d.map((d) => ({ ...d, date: formatChartDate(d.date) }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <ChartTooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="like"
                  name="Лайки"
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="comment"
                  name="Комменты"
                  stroke="#9c27b0"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="subscribe"
                  name="Подписки"
                  stroke="#ed6c02"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Recent actions */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Последние действия
            </Typography>
            <Button size="small" href="history">
              Вся история →
            </Button>
          </Box>
          {stats.recentActions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Действий пока нет — запустите конфигурацию автолайкера/автокоммент/автоподписки.
            </Typography>
          ) : (
            <List dense>
              {stats.recentActions.slice(0, 10).map((action: any) => (
                <ListItem key={action.id} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={TYPE_LABEL[action.actionType]?.ru || action.actionType}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={action.status}
                          size="small"
                          color={action.status === 'success' ? 'success' : 'error'}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {action.account?.firstName} {action.account?.lastName}
                        </Typography>
                      </Box>
                    }
                    secondary={new Date(action.createdAt).toLocaleString('ru-RU')}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
